import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../api/apiFetch';
import { 
  ChevronDown, 
  User, 
  ChevronRight, 
  X,
  RotateCw, Plus, Bot, MessageSquare, Menu, Home, Database,
  Edit2, Trash2, Save
} from 'lucide-react';
import FactEvolutionModal from './FactEvolutionModal';
import { diffWords } from '../../utils/DiffUtils';
import './HistoryTimeline.css';

interface HistoryLog {
  id: string;
  _id: string;
  mongo_id?: string;
  memory_id: string;
  old_memory: any;
  new_memory: any;
  event: string;
  created_at: string;
  actor_id: string;
  role?: string;
  session_id: string;
  is_deleted?: boolean;
}

const renderDiff = (oldVal: any, newVal: any, event: string, onlyNew = false, useYellow = false) => {
  const oldText = !oldVal ? "" : (typeof oldVal === 'string' ? oldVal : JSON.stringify(oldVal));
  const newText = !newVal ? "" : (typeof newVal === 'string' ? newVal : JSON.stringify(newVal));

  const eventLower = event.toLowerCase();
  
  const addClass = useYellow ? "diff-added-yellow" : "diff-added-inline";
  const remClass = "diff-removed-inline";
  
  if (eventLower.includes('add') || eventLower.includes('extract')) {
    return <span className={addClass}>{newText}</span>;
  }
  if (eventLower.includes('delete')) {
    return <span className={remClass}>{oldText || newText}</span>;
  }
  
  if (eventLower.includes('update')) {
    const diff = diffWords(oldText, newText);
    const res: React.ReactNode[] = [];
    
    diff.forEach((part, i) => {
      if (part.added) {
        res.push(<span key={i} className={addClass}>{part.value}</span>);
      } else if (part.removed) {
        if (!onlyNew) {
           res.push(<span key={i} className={remClass}>{part.value}</span>);
        }
      } else {
        res.push(part.value);
      }
    });

    return <div className="diff-res-container">{res}</div>;
  }

  return <span>{newText}</span>;
};

const HistoryTimelineItem: React.FC<{
  item: HistoryLog;
  formatDate: (d: string) => string;
  collapseSignal: number;
  expandSignal: number;
  onFactClick: (id: string) => void;
}> = ({ item, formatDate, onFactClick }) => {
  const getMemoryColor = (id: string) => {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
       hash = id.charCodeAt(i) + ((hash << 5) - hash);
    }
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
    let h = Math.abs(hash) % 360;
    
    if (!isDarkMode && h >= 240 && h <= 320) {
      h = (h + 100) % 360;
    }
    
    const s = isDarkMode ? 70 : 45;
    const l = isDarkMode ? 65 : 40;
    return `hsl(${h}, ${s}%, ${l}%)`;
  };

  const memoryColor = getMemoryColor(item.memory_id);

  return (
    <div className="timeline-item always-open" data-event={item.is_deleted ? 'delete' : item.event.toLowerCase()}>
        <div className="timeline-date">
          {formatDate(item.created_at)}
        </div>
        <div className="history-card simplified-card">
          <div className="card-metadata-header">
            <div className="metadata-left">
              <div className="history-event-badge" data-event={item.is_deleted ? 'delete' : item.event.toLowerCase()}>
                {item.is_deleted ? 'DELETE' : item.event}
              </div>
              <span
                className="history-fact-id-link"
                style={{ '--memory-color': memoryColor } as React.CSSProperties}
                onClick={() => onFactClick(item.memory_id)}
                title="View Fact Evolution"
              >
                <RotateCw size={12} />
                Fact: {item.memory_id}
              </span>
            </div>
          </div>

          <div className="card-diff-container">
            {(item.event.toLowerCase().includes('update') || item.event.toLowerCase().includes('delete') || item.is_deleted) ? (
              <div className="card-lineage-split">
                <div className="lineage-block old">
                  <div className="lineage-title">Previous Fact</div>
                  <div className="lineage-body">
                    {item.old_memory ? (typeof item.old_memory === 'string' ? item.old_memory : JSON.stringify(item.old_memory)) : "N/A"}
                  </div>
                </div>
                <div className="lineage-block new">
                  <div className="lineage-title">New Fact</div>
                  <div className="lineage-body">
                    {(item.event.toLowerCase().includes('delete') || item.is_deleted) ? (
                      <span className="deleted-placeholder">Fact Deleted</span>
                    ) : (
                      renderDiff(item.old_memory, item.new_memory, item.event, true)
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="single-fact-content">
                <div className="lineage-title">Fact Content</div>
                <div className="lineage-body">
                  {renderDiff(item.old_memory, item.new_memory, item.event)}
                </div>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

const HistoryTimeline: React.FC = () => {
  const [data, setData] = useState<HistoryLog[]>([]);
  const [revealedCount, setRevealedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('created_at:desc');
  const [collapseSignal, setCollapseSignal] = useState(0);
  const [expandSignal, setExpandSignal] = useState(0);
  const [showSidebar, setShowSidebar] = useState(true);
  const [activeUser, setActiveUser] = useState<string | null>(null);
  const [activeAgent, setActiveAgent] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  
  const [availableUsers, setAvailableUsers] = useState<{value: string, count: number}[]>([]);
  const [availableAgents, setAvailableAgents] = useState<Record<string, {value: string, count: number}[]>>({}); 
  const [availableRuns, setAvailableRuns] = useState<Record<string, {value: string, count: number}[]>>({});
  const [navigatorSearch, setNavigatorSearch] = useState('');

  const [selectedFact, setSelectedFact] = useState<string | null>(null);

  const filteredUsers = availableUsers.filter(user => 
    user.value.toLowerCase().includes(navigatorSearch.toLowerCase())
  );

  const toggleNode = async (nodeId: string, type?: 'user' | 'agent', value?: string, parentValue?: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });

    if (type === 'user' && value && !availableAgents[value]) {
       fetchFilterOptions('agent_id', { user_id: value });
    } else if (type === 'agent' && value && parentValue) {
       const key = `${parentValue}-${value}`;
       if (!availableRuns[key]) {
         fetchFilterOptions('run_id', { user_id: parentValue, agent_id: value });
       }
    }
  };

  const fetchFilterOptions = async (field: string, filters: Record<string, string> = {}) => {
    try {
      const queryParams = new URLSearchParams({ ...filters, collection: 'memory_audit_log' }).toString();
      const resp = await apiFetch(`/api/v1/memory/filter-options?${queryParams}`);
      if (resp.ok) {
        const json = await resp.json();
        console.log(`[NAVIGATOR] Filter options for ${field}:`, json);
        if (field === 'user_id') setAvailableUsers(json.users || []);
        else if (field === 'agent_id' && filters.user_id) {
          setAvailableAgents(prev => ({ ...prev, [filters.user_id]: json.agents || [] }));
        }
        else if (field === 'run_id' && filters.user_id && filters.agent_id) {
          const key = `${filters.user_id}-${filters.agent_id}`;
          setAvailableRuns(prev => ({ ...prev, [key]: json.sessions || [] }));
        }
      }
    } catch (err) {
      console.error(`Failed to fetch filter options for ${field}:`, err);
    }
  };

  const formatRelativeDate = (dateStr: string) => {
    if (!dateStr) return 'Unknown Date';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setCollapseSignal(s => s + 1);
    try {
      const [sortField, sortOrder] = sortBy.split(':');
      const sortDesc = sortOrder === 'desc';
      
      let url = `/api/v1/memory/history?sort_by=${sortField}&sort_desc=${sortDesc}&limit=500`;
      if (activeUser) url += `&user_id=${encodeURIComponent(activeUser)}`;
      if (activeAgent) url += `&agent_id=${encodeURIComponent(activeAgent)}`;
      if (activeRun) url += `&session_id=${encodeURIComponent(activeRun)}`;

      const resp = await apiFetch(url);
      if (!resp.ok) throw new Error(`API Error: ${resp.status} ${resp.statusText}`);
      
      const json = await resp.json();
      const actualData = json.data || (Array.isArray(json) ? json : []);
      
      setData(actualData);
      setIsInitialLoad(false);
      setRevealedCount(Math.min(10, actualData.length));
    } catch (err: any) {
      console.error('Failed to fetch history:', err);
      setError(err.message || 'Failed to connect to history service.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions('user_id');
  }, []);

  useEffect(() => {
    fetchData();
  }, [sortBy, activeUser, activeAgent, activeRun]);

  // Handled by FactEvolutionModal

  const handleFactClick = (memoryId: string) => {
    setSelectedFact(memoryId);
  };

  const handleRevealAll = () => {
    setRevealedCount(data.length);
    setExpandSignal(s => s + 1);
  };
  const handleRevealNext = () => setRevealedCount(prev => Math.min(prev + 10, data.length));
  const handleRefresh = () => fetchData();

  const renderBreadcrumbs = () => {
    const crumbs: { label: React.ReactNode; action: () => void }[] = [
      { label: <Home size={14} />, action: () => { setActiveUser(null); setActiveAgent(null); setActiveRun(null); } }
    ];
    
    if (activeUser) crumbs.push({ label: <><User size={14} /><span>{activeUser}</span></>, action: () => { setActiveAgent(null); setActiveRun(null); } });
    if (activeAgent) crumbs.push({ label: <><Bot size={14} /><span>{activeAgent}</span></>, action: () => { setActiveRun(null); } });
    if (activeRun) crumbs.push({ label: <><MessageSquare size={14} /><span>{activeRun}</span></>, action: () => {} });

    return (
      <div className="breadcrumb-container">
        {crumbs.map((crumb, idx) => (
          <React.Fragment key={idx}>
            <span className={`breadcrumb-item ${idx === crumbs.length - 1 ? 'active' : ''}`} onClick={crumb.action}>{crumb.label}</span>
            {idx < crumbs.length - 1 && <ChevronRight size={12} className="breadcrumb-separator" />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const itemsToRender = data;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return dateStr;
    }
  };

  if (isInitialLoad && loading) {
    return (
      <div className="timeline-loading">
        <RotateCw size={32} className="animate-spin" />
        <p>Loading History Logs...</p>
      </div>
    );
  }

  return (
    <div className="timeline-page-container">
      <div className={`timeline-sidebar ${showSidebar ? '' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            <h3>History Navigator</h3>
            <button className="sidebar-toggle-btn" onClick={() => setShowSidebar(false)} title="Close Navigator"><X size={18} /></button>
          </div>
          <div className="navigator-search">
            <div className="search-input-wrapper">
               <input 
                 type="text" 
                 placeholder="Search profiles..." 
                 value={navigatorSearch}
                 onChange={(e) => setNavigatorSearch(e.target.value)}
                 className="search-field"
               />
               {navigatorSearch && (
                 <button className="clear-search" onClick={() => setNavigatorSearch('')} title="Clear search">
                   <Plus size={14} className="rotate-45" />
                 </button>
               )}
            </div>
          </div>
        </div>
        <div className="tree-container">
           <div className={`tree-node root-node ${!activeUser ? 'active' : ''}`} onClick={() => { setActiveUser(null); setActiveAgent(null); setActiveRun(null); }}>
             <Home size={14} />
             <span className="root-label">All Logs</span>
           </div>
           
           {filteredUsers.map(user => {
             const uid = user.value;
             const isUserExpanded = expandedNodes.has(`user-${uid}`);
             const isUserActive = activeUser === uid;
             const agents = availableAgents[uid] || [];

             return (
               <div key={uid} className="tree-branch">
                 <div className={`tree-node user-node ${isUserActive ? 'active' : ''}`} onClick={() => { setActiveUser(uid); setActiveAgent(null); setActiveRun(null); toggleNode(`user-${uid}`, 'user', uid); }}>
                   {isUserExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                   <User size={14} />
                   <span className="tree-node-label">{uid} <span className="tree-node-count">({user.count})</span></span>
                 </div>
                 {isUserExpanded && (
                   <div className="tree-children">
                     {agents.length === 0 && <div className="tree-loading">Loading...</div>}
                     {agents.map(agent => {
                       const aid = agent.value;
                       const agentNodeId = `user-${uid}-agent-${aid}`;
                       const isAgentExpanded = expandedNodes.has(agentNodeId);
                       const isAgentActive = activeUser === uid && activeAgent === aid;
                       const runs = availableRuns[`${uid}-${aid}`] || [];

                       return (
                         <div key={aid} className="tree-branch">
                           <div className={`tree-node agent-node ${isAgentActive ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setActiveUser(uid); setActiveAgent(aid); setActiveRun(null); toggleNode(agentNodeId, 'agent', aid, uid); }}>
                             {isAgentExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                             <Bot size={14} />
                             <span className="tree-node-label">{aid} <span className="tree-node-count">({agent.count})</span></span>
                           </div>
                           {isAgentExpanded && (
                             <div className="tree-children">
                               {runs.length === 0 && <div className="tree-loading">Loading...</div>}
                               {runs.map(run => {
                                 const rid = run.value;
                                 const isRunActive = activeUser === uid && activeAgent === aid && activeRun === rid;
                                 return (
                                   <div key={rid} className={`tree-node run-node ${isRunActive ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setActiveUser(uid); setActiveAgent(aid); setActiveRun(rid); }}>
                                     <MessageSquare size={14} />
                                     <span className="tree-node-label">{rid.substring(0, 12)}... <span className="tree-node-count">({run.count})</span></span>
                                   </div>
                                 );
                               })}
                             </div>
                           )}
                         </div>
                       );
                     })}
                   </div>
                 )}
               </div>
             );
           })}
        </div>
      </div>

      <div className="timeline-main-content">
        <div className="memory-timeline">
           <div className="timeline-actions">
             <div className="action-group">
               {!showSidebar && <button className="sidebar-toggle-btn-minimal" onClick={() => setShowSidebar(true)} title="Expand Navigator"><Menu size={18} /></button>}
               {renderBreadcrumbs()}
             </div>
             {error && <div className="timeline-error-banner">{error}</div>}
             <div className="action-group">
               <select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} title="Sort Order">
                 <option value="created_at:desc">Newest First</option>
                 <option value="created_at:asc">Oldest First</option>
               </select>
               <button className="reveal-btn-minimal" onClick={handleRevealAll}>Reveal All</button>
               <button className="reveal-btn-minimal reveal-btn-ghost" onClick={() => { setRevealedCount(0); setCollapseSignal(s => s + 1); }}>Hide All</button>
               <button className="sort-btn" onClick={handleRefresh} title="Refresh Data"><RotateCw size={16} className={loading ? 'animate-spin' : ''} /></button>
             </div>
           </div>

           <div className="timeline-stats">
             Showing {revealedCount} of {itemsToRender.length} evolution steps
           </div>

           <div className="timeline-stream">
              {loading && (
                <div className="stream-spinner">
                  <RotateCw size={32} className="animate-spin" />
                </div>
              )}

              {!loading && itemsToRender.length > 0 && (() => {
                const finalToRender: any[] = [];
                let lastDateHeader = '';
                
                const actualCount = Math.min(revealedCount, itemsToRender.length);
                for (let i = 0; i < actualCount; i++) {
                  const item = itemsToRender[i];
                  const currentDate = formatRelativeDate(item.created_at);
                  if (currentDate !== lastDateHeader) {
                    finalToRender.push({ type: 'date-header', label: currentDate, key: `date-${currentDate}-${i}` });
                    lastDateHeader = currentDate;
                  }
                  finalToRender.push({ type: 'item', item, key: item._id || `item-${i}` });
                }

                return finalToRender.map((node) => {
                  if (node.type === 'date-header') {
                    return <div key={node.key} className="date-group-header"><span>{node.label}</span></div>;
                  }
                  return (
                    <HistoryTimelineItem 
                      key={node.key}
                      item={node.item}
                      formatDate={formatDate}
                      collapseSignal={collapseSignal}
                      expandSignal={expandSignal}
                      onFactClick={handleFactClick}
                    />
                  );
                });
              })()}

              {!loading && revealedCount < itemsToRender.length && (
                <div className="timeline-item reveal-more-item" onClick={handleRevealNext}>
                  <div className="timeline-dot-plus"><Plus size={8} color="#fff" strokeWidth={4} /></div>
                  <div className="timeline-date reveal-more-text">Reveal more historical logs</div>
                </div>
              )}

              {!loading && itemsToRender.length === 0 && (
                <div className="empty-context-state">
                  <div className="empty-state-icon">
                     <Database size={48} />
                  </div>
                  <h3>Trace Fact Evolution</h3>
                  <p>Select a profile from the History Navigator to visualize how facts were extracted and refined across the system lifecycle.</p>
                  <div className="empty-state-hint">
                     <ChevronRight size={14} className="rotate-180" />
                     <span>Select from sidebar</span>
                  </div>
                </div>
              )}
            </div>
        </div>
      </div>

      {selectedFact && (
        <FactEvolutionModal 
          memoryId={selectedFact} 
          onClose={() => setSelectedFact(null)} 
          onDataChanged={fetchData}
        />
      )}
    </div>
  );
};

export default HistoryTimeline;
